import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DropzoneContentComponent } from '@modules/shared/dropzone-content/dropzone-content.component';
import { ImageOverlayComponent } from '@modules/shared/image-overlay/image-overlay.component';
import { Media } from '@pixacare/domain';
import { formatFileSize } from '@shared/utils/format-file-size';
import { TuiError, TuiIcon, TuiTitle } from '@taiga-ui/core';
import { TuiFileLike, TuiFiles } from '@taiga-ui/kit';
import { MediaListGroup } from '../../models/media-list-group';
import { MediaListItem } from '../../models/media-list-item';
import { MediaListItemContext } from '../../models/media-list-item-context';
import { MediaPickerValidator } from '../../models/media-picker-validator';
import { getFileKey, getMediaKey, getMediaListItemKey } from '../../utils/media-picker-key';
import { MediaListComponent } from '../media-list/media-list.component';

// Taiga's tuiInputFiles default — kept as the fallback so image-only consumers are unaffected.
const DEFAULT_MAX_FILE_SIZE = 30 * 1024 * 1024;

@Component({
  selector: 'pxc-media-picker',
  exportAs: 'pxcMediaPicker',
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    TuiError,
    TuiFiles,
    TuiIcon,
    TuiTitle,
    DropzoneContentComponent,
    ImageOverlayComponent,
    MediaListComponent,
  ],
  templateUrl: './media-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex min-h-0 flex-col gap-6',
  },
})
export class MediaPickerComponent {
  readonly groups = input<MediaListGroup[]>([]);
  readonly preselectedMediaIds = input<number[]>([]);
  readonly preselectedFiles = input<File[]>([]);
  readonly maxSelectable = input<number>();
  readonly validators = input<MediaPickerValidator[]>([]);
  readonly itemTemplate = input<TemplateRef<MediaListItemContext>>();
  readonly isItemSelectable = input<(item: MediaListItem) => boolean>(() => true);
  readonly accept = input('image/*');
  readonly maxFileSize = input<number>();
  readonly hasMore = input(false);
  readonly isLoadingMore = input(false);
  readonly isLongVideoEnabled = input(false);

  protected readonly effectiveMaxFileSize = computed(() => this.maxFileSize() ?? DEFAULT_MAX_FILE_SIZE);
  protected readonly fileTooLargeMessage = computed(() =>
    this.translocoService.translate('Fichier trop volumineux ({{maxSize}} maximum)', {
      maxSize: formatFileSize(this.effectiveMaxFileSize()),
    }),
  );

  readonly uploadedFilesChange = output<File[]>();
  readonly loadMoreRequest = output<void>();

  protected readonly selectedKeys = linkedSignal<Set<string>>(
    () =>
      new Set([
        ...this.preselectedMediaIds().map((id) => getMediaKey(id)),
        ...this.preselectedFiles().map((file) => getFileKey(file)),
      ]),
  );
  protected readonly uploadedFiles = linkedSignal(() => [...this.preselectedFiles()]);
  protected readonly rejectedFiles = signal<readonly TuiFileLike[]>([]);

  readonly selectedCount = computed(() => this.selectedKeys().size);

  readonly selection = computed(() => {
    const keys = this.selectedKeys();
    const medias: Media[] = [];
    const files: File[] = [];
    const seenFileKeys = new Set<string>();
    // Group items may be either medias or pre-existing files; uploaded files are tracked separately.
    for (const group of this.groups()) {
      for (const item of group.items) {
        if (item.type === 'media' && keys.has(getMediaKey(item.media.id))) {
          medias.push(item.media);
        } else if (item.type === 'file' && keys.has(getFileKey(item.file))) {
          files.push(item.file);
          seenFileKeys.add(getFileKey(item.file));
        }
      }
    }
    for (const file of this.uploadedFiles()) {
      const key = getFileKey(file);
      if (keys.has(key) && !seenFileKeys.has(key)) {
        files.push(file);
        seenFileKeys.add(key);
      }
    }
    return { medias, files };
  });

  readonly validationError = computed<string | null>(() => {
    const selection = this.selection();
    for (const validator of this.validators()) {
      const error = validator(selection);
      if (error) {
        return error;
      }
    }
    return null;
  });

  readonly isConfirmDisabled = computed(() => {
    const size = this.selectedKeys().size;
    if (size === 0) {
      return true;
    }
    const max = this.maxSelectable();
    if (max !== undefined && size > max) {
      return true;
    }
    return this.validationError() !== null;
  });

  private readonly translocoService = inject(TranslocoService);

  protected isTooLarge(file: TuiFileLike): boolean {
    return (file.size ?? 0) > this.effectiveMaxFileSize();
  }

  protected onItemToggled(item: MediaListItem): void {
    const key = getMediaListItemKey(item);
    this.selectedKeys.update((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      const max = this.maxSelectable();
      if (max === 1) {
        return new Set([key]);
      }
      if (max !== undefined && next.size >= max) {
        return next;
      }
      next.add(key);
      return next;
    });
  }

  protected onGroupToggled(group: MediaListGroup): void {
    const isSelectable = this.isItemSelectable();
    const keys = group.items.filter((item) => isSelectable(item)).map((item) => getMediaListItemKey(item));
    if (keys.length === 0) {
      return;
    }
    this.selectedKeys.update((current) => {
      const next = new Set(current);
      const allSelected = keys.every((key) => next.has(key));
      if (allSelected) {
        for (const key of keys) {
          next.delete(key);
        }
        return next;
      }
      const max = this.maxSelectable();
      for (const key of keys) {
        if (max !== undefined && next.size >= max) {
          break;
        }
        next.add(key);
      }
      return next;
    });
  }

  protected onFilesAdded(files: File | File[] | null): void {
    // `tuiInputFiles` emits a single File in single-select mode ([multiple]="false")
    // and an array in multi-select mode; normalize to an array.
    const incoming = Array.isArray(files) ? files : files ? [files] : [];
    const rejected = this.rejectedFiles();
    const accepted = incoming.filter((file) => !rejected.some((r) => r.name === file.name));
    const existing = this.uploadedFiles();
    const merged = [...existing];
    let newlySelected = new Set(this.selectedKeys());
    const max = this.maxSelectable();
    for (const file of accepted) {
      const key = getFileKey(file);
      const isNew = !merged.some((f) => getFileKey(f) === key);
      if (!isNew) {
        continue;
      }
      merged.push(file);
      if (max === 1) {
        newlySelected = new Set([key]);
        break;
      }
      if (max === undefined || newlySelected.size < max) {
        newlySelected.add(key);
      }
    }
    this.uploadedFiles.set(merged);
    this.selectedKeys.set(newlySelected);
    this.uploadedFilesChange.emit(merged);
  }

  protected onReject(files: TuiFileLike | readonly TuiFileLike[]): void {
    const incoming = Array.isArray(files) ? files : [files as TuiFileLike];
    this.rejectedFiles.update((current) => [...current, ...incoming]);
  }

  protected removeRejected({ name }: TuiFileLike): void {
    this.rejectedFiles.update((current) => current.filter((rejected) => rejected.name !== name));
  }
}
